import { supabase } from '../../lib/supabase';
import type { IdeaRow, InsightRow, QuoteRow, ProductRow, BusinessPlanRow } from '../../types/database';
import type { Idea, Insight, Quote, Product, Episode, Podcast } from '../../types/index';
import type { EnhancedIdea } from '../../types';

type IdeaSourceRow = IdeaRow & {
  user_vote?: 'upvote' | 'downvote' | null;
  is_saved?: boolean;
  currency?: string | null;
};

function mapIdeaRow(row: IdeaSourceRow): Idea {
  return {
    id: row.id,
    title: row.title ?? '',
    short_description: row.short_description ?? '',
    detailed_description: row.detailed_description ?? '',
    market_size: row.market_size ?? 0,
    normalized_market_size_usd: row.normalized_market_size_usd ?? undefined,
    timeline: row.timeline ?? undefined,
    currency: row.currency ?? undefined,
    business_area: row.business_area ?? '',
    episode_id: row.episode_id,
    upvotes: row.upvotes ?? 0,
    downvotes: row.downvotes ?? 0,
    created_at: row.created_at ?? '',
    user_vote: row.user_vote ?? undefined,
    is_saved: row.is_saved ?? undefined,
  };
}

function mapInsightRow(row: InsightRow): Insight {
  return {
    id: row.id,
    title: row.title ?? '',
    description: row.description ?? '',
    episode_id: row.episode_id,
    ai_quality_score: row.ai_quality_score ?? undefined,
    created_at: row.created_at ?? '',
  };
}

function mapQuoteRow(row: QuoteRow): Quote {
  const timestamp =
    row.timestamp_seconds != null
      ? `[${Math.floor(row.timestamp_seconds / 60)}:${String(row.timestamp_seconds % 60).padStart(2, '0')}]`
      : undefined;
  return {
    id: row.id,
    text: row.text ?? '',
    speaker: row.speaker ?? '',
    timestamp,
    episode_id: row.episode_id,
    ai_quality_score: row.ai_quality_score ?? undefined,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}

function mapProductRow(row: ProductRow): Product {
  return {
    id: row.id,
    name: row.name ?? '',
    description: row.description ?? '',
    url: row.url ?? undefined,
    episode_id: row.episode_id,
    created_at: row.created_at ?? '',
    updated_at: '',
  };
}

function toTimestampLabel(timestampSeconds: number | null | undefined): string {
  if (timestampSeconds == null) return '';
  return `[${Math.floor(timestampSeconds / 60)}:${String(timestampSeconds % 60).padStart(2, '0')}]`;
}

// --------------- Ideas ---------------

export async function getIdeas(limit: number = 50): Promise<Idea[]> {
  const { data, error } = await supabase
    .from('ideas')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(`Failed to fetch ideas: ${error.message}`);
  return (data ?? []).map((r) => mapIdeaRow(r as IdeaRow));
}

export async function getIdeasByEpisodeId(episodeId: string): Promise<Idea[]> {
  const { data, error } = await supabase
    .from('ideas')
    .select('*')
    .eq('episode_id', episodeId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Failed to fetch ideas: ${error.message}`);
  return (data ?? []).map((r) => mapIdeaRow(r as IdeaRow));
}

export async function getIdeaById(id: string): Promise<Idea | null> {
  const { data, error } = await supabase
    .from('ideas')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(`Failed to fetch idea: ${error.message}`);
  }
  return data ? mapIdeaRow(data as IdeaRow) : null;
}

/** Params for paginated ideas list (Explore Ideas page). */
export interface GetAllIdeasParams {
  page?: number;
  sortBy?: string;
  sortOrder?: string;
  businessArea?: string;
  keyword?: string;
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * Get paginated ideas with episode + podcast (Explore Ideas page).
 * Fetches from Supabase with filters and sort.
 */
export async function getAllIdeas(params: GetAllIdeasParams = {}): Promise<{
  items: EnhancedIdea[];
  total_pages: number;
  total?: number;
  page?: number;
  page_size?: number;
}> {
  const page = Math.max(1, params.page ?? 1);
  const sortBy = params.sortBy ?? 'upvotes';
  const sortOrder = params.sortOrder ?? 'desc';
  const businessArea = params.businessArea ?? '';
  const keyword = (params.keyword ?? '').trim();
  const pageSize = DEFAULT_PAGE_SIZE;

  let query = supabase
    .from('ideas')
    .select(
      `
      *,
      episode:episodes (
        id,
        title,
        description,
        thumbnail_url,
        youtube_url,
        guest_name,
        summary,
        podcast_id,
        published_date,
        podcast:podcasts (
          id,
          title,
          description,
          channel_name,
          channel_url,
          subscriber_count,
          video_count
        )
      )
    `,
      { count: 'exact' }
    );

  if (businessArea) {
    query = query.eq('business_area', businessArea);
  }

  if (keyword) {
    query = query.or(`title.ilike.%${keyword}%,short_description.ilike.%${keyword}%`);
  }

  const orderColumn =
    sortBy === 'created_at' ? 'created_at' : sortBy === 'upvotes' ? 'upvotes' : 'upvotes';
  query = query.order(orderColumn, { ascending: sortOrder === 'asc' });

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  const { data, error, count } = await query.range(from, to);

  if (error) {
    throw new Error(`Failed to fetch ideas: ${error.message}`);
  }

  const rows = (data ?? []) as Array<
    IdeaRow & {
      episode?: {
        id: string;
        title: string | null;
        description: string | null;
        thumbnail_url: string | null;
        youtube_url: string | null;
        guest_name: string | null;
        summary: string | null;
        podcast_id: string;
        published_date?: string | null;
        podcast?: {
          id: string;
          title: string | null;
          description: string | null;
          channel_name: string | null;
          channel_url: string | null;
          thumbnail_url?: string | null;
          subscriber_count?: number | null;
          video_count?: number | null;
        };
      };
    }
  >;

  const items: EnhancedIdea[] = rows.map((row) => {
    const idea = mapIdeaRow(row);
    const ep = row.episode;
    const pub = ep?.published_date ?? '';
    const episode = ep
      ? {
          id: ep.id,
          title: ep.title ?? '',
          description: ep.description ?? '',
          thumbnail_url: ep.thumbnail_url ?? '',
          youtube_url: ep.youtube_url ?? '',
          guest_name: ep.guest_name ?? undefined,
          summary: ep.summary ?? undefined,
          podcast_id: ep.podcast_id,
          published_date: pub,
          published_at: pub,
        }
      : ({} as Episode);
    const p = ep?.podcast;
    const podcast: Podcast = p
      ? {
        id: p.id,
        title: p.title ?? '',
        description: p.description ?? '',
        channel_name: p.channel_name ?? '',
        channel_url: p.channel_url ?? '',
        thumbnail_url: p.thumbnail_url ?? undefined,
        subscriber_count: p.subscriber_count ?? undefined,
      }
      : ({} as Podcast);
    return { ...idea, episode, podcast } as EnhancedIdea;
  });

  const total = count ?? 0;
  const total_pages = Math.max(1, Math.ceil(total / pageSize));

  return {
    items,
    total_pages,
    total,
    page,
    page_size: pageSize,
  };
}

/**
 * Get distinct business areas from ideas (for Explore Ideas filter).
 */
export async function getBusinessAreas(): Promise<string[]> {
  const { data, error } = await supabase
    .from('ideas')
    .select('business_area')
    .not('business_area', 'is', null);

  if (error) {
    throw new Error(`Failed to fetch business areas: ${error.message}`);
  }

  const areas = [...new Set((data ?? []).map((r) => (r as { business_area: string }).business_area).filter(Boolean))];
  return areas.sort((a, b) => a.localeCompare(b));
}

// --------------- Insights ---------------

export async function getInsights(limit: number = 50): Promise<Insight[]> {
  const { data, error } = await supabase
    .from('insights')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(`Failed to fetch insights: ${error.message}`);
  return (data ?? []).map((r) => mapInsightRow(r as InsightRow));
}

export async function getInsightsByEpisodeId(episodeId: string): Promise<Insight[]> {
  const { data, error } = await supabase
    .from('insights')
    .select('*')
    .eq('episode_id', episodeId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Failed to fetch insights: ${error.message}`);
  return (data ?? []).map((r) => mapInsightRow(r as InsightRow));
}

// --------------- Quotes ---------------

export async function getQuotes(limit: number = 50): Promise<Quote[]> {
  const { data, error } = await supabase
    .from('quotes')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw new Error(`Failed to fetch quotes: ${error.message}`);
  return (data ?? []).map((r) => mapQuoteRow(r as QuoteRow));
}

export async function getQuotesByEpisodeId(episodeId: string): Promise<Quote[]> {
  const { data, error } = await supabase
    .from('quotes')
    .select('*')
    .eq('episode_id', episodeId)
    .order('timestamp_seconds', { ascending: true });

  if (error) throw new Error(`Failed to fetch quotes: ${error.message}`);
  return (data ?? []).map((r) => mapQuoteRow(r as QuoteRow));
}

// --------------- Products ---------------

export async function getProductsByEpisodeId(episodeId: string): Promise<Product[]> {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('episode_id', episodeId);

  if (error) throw new Error(`Failed to fetch products: ${error.message}`);
  return (data ?? []).map((r) => mapProductRow(r as ProductRow));
}

export interface FeaturedInsight extends Insight {
  episode: Episode;
  podcast: Podcast;
}

export async function getRandomInsights(
  count: number = 4,
  minQualityScore: number = 70
): Promise<FeaturedInsight[]> {
  const sampleSize = Math.max(count * 5, 40);

  const { data, error } = await supabase
    .from('insights')
    .select(
      `
      *,
      episode:episodes (
        id,
        title,
        description,
        thumbnail_url,
        youtube_url,
        guest_name,
        summary,
        podcast_id,
        published_date,
        podcast:podcasts (
          id,
          title,
          description,
          channel_name,
          channel_url,
          thumbnail_url,
          subscriber_count
        )
      )
    `
    )
    .gte('ai_quality_score', minQualityScore)
    .order('created_at', { ascending: false })
    .limit(sampleSize);

  if (error) {
    throw new Error(`Failed to fetch random insights: ${error.message}`);
  }

  const rows = (data ?? []) as Array<
    InsightRow & {
      episode?: {
        id: string;
        title: string | null;
        description: string | null;
        thumbnail_url: string | null;
        youtube_url: string | null;
        guest_name: string | null;
        summary: string | null;
        podcast_id: string;
        published_date?: string | null;
        podcast?: {
          id: string;
          title: string | null;
          description: string | null;
          channel_name: string | null;
          channel_url: string | null;
          thumbnail_url?: string | null;
          subscriber_count?: number | null;
        };
      };
    }
  >;

  const mapped: FeaturedInsight[] = rows
    .filter((row) => row.episode?.podcast)
    .map((row) => {
      const insight = mapInsightRow(row);
      const ep = row.episode!;
      const pod = ep.podcast!;

      const episode: Episode = {
        id: ep.id,
        title: ep.title ?? '',
        description: ep.description ?? '',
        thumbnail_url: ep.thumbnail_url ?? '',
        youtube_url: ep.youtube_url ?? '',
        guest_name: ep.guest_name ?? undefined,
        summary: ep.summary ?? undefined,
        podcast_id: ep.podcast_id,
        published_date: ep.published_date ?? '',
      };

      const podcast: Podcast = {
        id: pod.id,
        title: pod.title ?? '',
        description: pod.description ?? '',
        channel_name: pod.channel_name ?? '',
        channel_url: pod.channel_url ?? '',
        thumbnail_url: pod.thumbnail_url ?? undefined,
        subscriber_count: pod.subscriber_count ?? undefined,
      };

      return { ...insight, episode, podcast };
    });

  const shuffled = [...mapped].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

interface PlanPhaseData {
  timeline?: string | null;
  steps?: unknown[];
}

function toDisplayString(value: unknown, fallback: string = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value && typeof value === 'object') {
    const maybeObject = value as { description?: unknown; text?: unknown };
    if (typeof maybeObject.description === 'string') return maybeObject.description;
    if (typeof maybeObject.text === 'string') return maybeObject.text;
  }
  return fallback;
}

function toOptionalString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value && typeof value === 'object') {
    const maybeObject = value as { description?: unknown; text?: unknown };
    if (typeof maybeObject.description === 'string') return maybeObject.description;
    if (typeof maybeObject.text === 'string') return maybeObject.text;
  }
  return null;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => toDisplayString(item, ''))
    .map((item) => item.trim())
    .filter(Boolean);
}

function toPlanPhaseData(value: unknown): PlanPhaseData {
  if (!value || typeof value !== 'object') return {};
  const maybePhase = value as { timeline?: unknown; steps?: unknown };
  const timeline = toOptionalString(maybePhase.timeline);
  const steps = Array.isArray(maybePhase.steps) ? maybePhase.steps : [];
  return { timeline, steps };
}

export interface IdeaDetailData {
  id: string;
  title: string;
  sourceEpisode: {
    id: string;
    title: string;
    timestamp: string;
    podcastId: string;
  };
  industryTags: string[];
  shortDescription: string;
  quotes: {
    text: string;
    speaker: string;
    timestamp: string;
  }[];
  metrics: {
    marketSize: string;
    timeline: string;
    initialInvestment: string;
    teamSize: string;
    competition: string;
    marketTrend: string;
  };
  businessPlan: {
    overview: string;
    valueProposition: string[];
    targetMarket: string[];
    revenueModel: string[];
    challenges: string[];
    opportunities: string[];
  };
  implementation: {
    [key: string]: {
      title: string;
      timeline: string;
      tasks: string[];
    };
  };
  user_vote?: 'upvote' | 'downvote' | null;
  upvotes: number;
  downvotes: number;
  is_saved: boolean;
}

export async function getIdeaDetailsById(ideaId: string): Promise<IdeaDetailData | null> {
  const { data: ideaData, error: ideaError } = await supabase
    .from('ideas')
    .select(
      `
      *,
      episode:episodes (
        id,
        title,
        podcast_id
      )
    `
    )
    .eq('id', ideaId)
    .single();

  if (ideaError) {
    if (ideaError.code === 'PGRST116') return null;
    throw new Error(`Failed to fetch idea details: ${ideaError.message}`);
  }

  const row = ideaData as IdeaRow & {
    episode?: {
      id: string;
      title: string | null;
      podcast_id: string;
    };
  };

  const [businessPlanResult, quotesResult] = await Promise.all([
    supabase.from('business_plans').select('*').eq('idea_id', ideaId).maybeSingle(),
    supabase
      .from('quotes')
      .select('*')
      .eq('episode_id', row.episode_id)
      .order('timestamp_seconds', { ascending: true })
      .limit(8),
  ]);

  if (businessPlanResult.error) {
    throw new Error(`Failed to fetch business plan: ${businessPlanResult.error.message}`);
  }
  if (quotesResult.error) {
    throw new Error(`Failed to fetch quotes: ${quotesResult.error.message}`);
  }

  const plan = (businessPlanResult.data as BusinessPlanRow | null) ?? null;
  const quotes = (quotesResult.data as QuoteRow[] | null) ?? [];

  const foundationSetup = toPlanPhaseData(plan?.foundation_setup);
  const programDevelopment = toPlanPhaseData(plan?.program_development);

  const marketSize =
    row.normalized_market_size_usd != null
      ? `$${Math.round(row.normalized_market_size_usd).toLocaleString()}`
      : plan?.market_size ?? '$0';

  return {
    id: row.id,
    title: row.title ?? '',
    sourceEpisode: {
      id: row.episode?.id ?? row.episode_id,
      title: row.episode?.title ?? '',
      timestamp: toTimestampLabel(quotes[0]?.timestamp_seconds),
      podcastId: row.episode?.podcast_id ?? '',
    },
    industryTags: row.business_area ? [row.business_area] : [],
    shortDescription: row.short_description ?? '',
    quotes: quotes.map((quote) => ({
      text: quote.text ?? '',
      speaker: quote.speaker ?? 'Speaker',
      timestamp: toTimestampLabel(quote.timestamp_seconds),
    })),
    metrics: {
      marketSize,
      timeline: toDisplayString(plan?.timeline ?? row.timeline, 'N/A'),
      initialInvestment: toDisplayString(plan?.initial_investment, 'N/A'),
      teamSize: toDisplayString(plan?.team_size, 'N/A'),
      competition: toDisplayString(plan?.competition_level, 'Low'),
      marketTrend: toDisplayString(plan?.market_trend, 'Stable'),
    },
    businessPlan: {
      overview: toDisplayString(plan?.detailed_description ?? row.detailed_description ?? row.short_description, ''),
      valueProposition: toStringArray(plan?.value_propositions),
      targetMarket: toStringArray(plan?.target_market),
      revenueModel: toStringArray(plan?.revenue_model),
      challenges: toStringArray(plan?.challenges),
      opportunities: toStringArray(plan?.opportunities),
    },
    implementation: {
      phase1: {
        title: 'Foundation Setup',
        timeline: foundationSetup.timeline ?? 'N/A',
        tasks: (foundationSetup.steps ?? []).map((step) => toDisplayString(step, '')).filter(Boolean),
      },
      phase2: {
        title: 'Program Development',
        timeline: programDevelopment.timeline ?? 'N/A',
        tasks: (programDevelopment.steps ?? []).map((step) => toDisplayString(step, '')).filter(Boolean),
      },
    },
    user_vote: null,
    upvotes: row.upvotes ?? 0,
    downvotes: row.downvotes ?? 0,
    is_saved: false,
  };
}

// --------------- Votes ---------------

/**
 * Vote on an idea (upvote or downvote).
 * This function calls the `vote_idea` RPC function which handles:
 * - Toggling votes (upvote → no vote → downvote → no vote)
 * - Updating vote counts
 * - Creating/updating/deleting vote records
 *
 * @param ideaId - The ID of the idea to vote on
 * @param voteType - 'upvote' or 'downvote'
 * @returns The updated idea with new vote counts and user_vote status
 */
export async function voteOnIdea(ideaId: string, voteType: 'upvote' | 'downvote'): Promise<Idea> {
  // Call the vote_idea RPC function
  const { data, error } = await supabase.rpc('vote_idea', {
    p_idea_id: ideaId,
    p_vote_type: voteType,
  });

  if (error) {
    console.error('Vote error:', error);
    throw new Error(`Failed to vote on idea: ${error.message}`);
  }

  // The RPC function returns the updated idea
  if (!data || data.length === 0) {
    throw new Error('No idea returned from vote operation');
  }

  return mapIdeaRow(data[0] as IdeaRow);
}
